import React, { useMemo } from "react";
import { isEqual } from "@common/helpers/comparison";
import { FiltersTypes } from "@common/types/filters/general";
import {
    FinancialAccount,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { CounterpartyDirection } from "@common/helpers/counterparties";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useResetFilters } from "@components/pageHeader/filters/ActiveFilters/useResetFilters";

import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import {
    DirectionRadioDef,
    glDirectionRadios,
} from "./Tabs/AmountFilter/directionRadios";
import {
    type ActiveFilter,
    ActiveFilterToFilterTabsMap,
    FiltersChangeFn,
    FilterTabs,
    resetPayload,
    VIRTUAL_FILTER_TABS,
} from "./lib";
import { ActiveFilterButtonLabel } from "./ActiveFilterButtonLabel";
import { FiltersOverlay } from "./FiltersOverlay";
import { CoaTemplateGroupOption } from "./Tabs/CoaTemplateGroupsFilter/CoaTemplateGroupsFilter.lib";
import { SchedulesReportFilterOption } from "./Tabs/SchedulesReportFilters/OptionsMultiselectFilter";
import { useActiveFilters } from "./useActiveFilters";
import { FilterMultiselectOption } from "./FilterMultiselect/FilterMultiselect";

export interface FiltersProps {
    filters: FiltersTypes;
    onChange: FiltersChangeFn;

    availableFilters: FilterTabs[];
    staticActiveFilters?: React.ReactNode;
    defaultFilters?: FiltersTypes;

    defaultTab?: FilterTabs;

    directionSwitchDisabled?: boolean;
    directionSwitchReadonly?: boolean;
    directionRadios?: DirectionRadioDef[];
    showAmountFilterLevel?: boolean;
    prioritizedActiveFilters?: ActiveFilter[];
    disabledActiveFilters?: ActiveFilter[];
    hardcodedEntityId?: number;
    availableClassifications?: AccountClass[];
    staticAccountTypeNodes?: Array<
        import("./Tabs/AccountTypeFilter/AccountTypeFilter").AccountTypeNode
    >;
    staticTemplateGroupOptions?: CoaTemplateGroupOption[];
    accountFilterSourceTypes?: FinancialAccountSourceType[];
    accountFilterPredicate?: (account: FinancialAccount) => boolean;
    onlyStandardCategories?: boolean;
    shouldShowNoAccountOption?: boolean;
    shouldShowKickAccountOption?: boolean;
    hideCoaAccountLastChangedBy?: boolean;
    /**
     * Strips the transaction-only extras from the counterparty, class and
     * ledger tabs (presence rows, match-mode settings, "Last changed by"),
     * leaving the value lists. Used by the rules page.
     */
    valuePickersOnly?: boolean;

    hideOverlay?: boolean;
    hideClearButton?: boolean;
    maxVisibleFilters?: number;
    disabled?: boolean;
    filtersContainerRef?: React.RefObject<HTMLDivElement | null>;
    /**
     * Word used in place of "counterparty" in the counterparty filter tab
     * and its active filter chip (e.g. "customer" on Invoices, "vendor" on
     * Bills). Defaults to the generic "Counterparty".
     */
    counterpartyEntityLabel?: string;
    counterpartyDirection?: CounterpartyDirection;
    counterpartySingleSelect?: boolean;
    /**
     * Restricts the counterparty filter's listed options to counterparties
     * used as the customer on at least one invoice. Used by the Invoices
     * page and AR Aging report filters.
     */
    counterpartyInvoicesOnly?: boolean;
    /** Task assignee filter lists organization members instead of workspace members (org-level tasks page). */
    hasTaskAssigneeOrgMembersOnly?: boolean;
    /** Adds an "Assigned to client" option to the task assignee filter (org-level tasks page). */
    hasTaskAssignedToClientOption?: boolean;
    /** Adds a "Client" option to the task created-by filter (org-level tasks page). */
    hasTaskCreatedByClientOption?: boolean;
    /**
     * Label for the ledger filter tab. Defaults to "General ledger".
     * Transactions overrides this to "Cash ledger".
     */
    ledgerFilterLabel?: string;
    staticHoldingAccountOptions?: SchedulesReportFilterOption[];
    /** Label of the filter trigger button. Defaults to "Filter". */
    filterButtonLabel?: string;
    tabLabels?: Partial<Record<FilterTabs, string>>;
    draftStatusOptions?: FilterMultiselectOption[];
    draftSourceOptions?: FilterMultiselectOption[];
}

function useFiltersActive(
    currentFilters: FiltersTypes,
    defaultFilters?: FiltersTypes,
): boolean {
    return useMemo(
        () => !defaultFilters || !isEqual(currentFilters, defaultFilters),
        [currentFilters, defaultFilters],
    );
}

function prioritizeActiveFilters(
    activeFilters: ActiveFilter[],
    prioritizedActiveFilters?: ActiveFilter[],
): ActiveFilter[] {
    if (!prioritizedActiveFilters?.length) {
        return activeFilters;
    }

    const prioritySet = new Set(prioritizedActiveFilters);

    return [
        ...prioritizedActiveFilters.filter((filterId) =>
            activeFilters.includes(filterId),
        ),
        ...activeFilters.filter((filterId) => !prioritySet.has(filterId)),
    ];
}

export const Filters: React.FC<FiltersProps> = ({
    filters,
    filtersContainerRef,
    onChange,
    availableFilters: availableFiltersProp,
    staticActiveFilters,
    defaultTab = FilterTabs.DATE,
    directionSwitchDisabled,
    directionSwitchReadonly,
    directionRadios,
    showAmountFilterLevel,
    prioritizedActiveFilters,
    disabledActiveFilters,
    defaultFilters,
    hardcodedEntityId,
    hideOverlay,
    hideClearButton,
    maxVisibleFilters,
    disabled = false,
    availableClassifications,
    staticAccountTypeNodes,
    staticTemplateGroupOptions,
    accountFilterSourceTypes,
    accountFilterPredicate,
    onlyStandardCategories,
    shouldShowNoAccountOption,
    shouldShowKickAccountOption,
    hideCoaAccountLastChangedBy,
    valuePickersOnly,
    counterpartyEntityLabel,
    counterpartyDirection,
    counterpartySingleSelect,
    counterpartyInvoicesOnly,
    hasTaskCreatedByClientOption,
    hasTaskAssigneeOrgMembersOnly,
    hasTaskAssignedToClientOption,
    ledgerFilterLabel,
    staticHoldingAccountOptions,
    filterButtonLabel,
    tabLabels,
    draftStatusOptions,
    draftSourceOptions,
}) => {
    // GL-first workspaces don't use transaction categories, so the category
    // filter must not be offered there.
    const { enabled: isGlFirst } = useIsGlFirst();
    const availableFilters = useMemo(
        () =>
            isGlFirst
                ? availableFiltersProp.filter(
                      (filterTab) => filterTab !== FilterTabs.CATEGORY,
                  )
                : availableFiltersProp,
        [availableFiltersProp, isGlFirst],
    );

    // this checks if there are any default filters applied
    const filtersActive = useFiltersActive(filters, defaultFilters);
    const activeFilters = useActiveFilters(filters, availableFilters);
    const orderedActiveFilters = useMemo(
        () => prioritizeActiveFilters(activeFilters, prioritizedActiveFilters),
        [activeFilters, prioritizedActiveFilters],
    );
    const resetActiveFilters = useResetFilters(
        resetPayload,
        onChange,
        orderedActiveFilters,
    );

    return (
        <AggregatedFiltersProvider
            key={availableFilters.join("|")}
            defaultTab={defaultTab}
        >
            {!hideOverlay && (
                <AggregatedFilters
                    disabled={disabled}
                    label={filterButtonLabel}
                >
                    <FiltersOverlay
                        showAmountFilterLevel={showAmountFilterLevel}
                        filters={filters}
                        onChange={onChange}
                        directionSwitchDisabled={directionSwitchDisabled}
                        directionSwitchReadonly={directionSwitchReadonly}
                        directionRadios={directionRadios}
                        availableFilters={availableFilters}
                        hardcodedEntityId={hardcodedEntityId}
                        availableClassifications={availableClassifications}
                        staticAccountTypeNodes={staticAccountTypeNodes}
                        staticTemplateGroupOptions={staticTemplateGroupOptions}
                        staticHoldingAccountOptions={
                            staticHoldingAccountOptions
                        }
                        accountFilterSourceTypes={accountFilterSourceTypes}
                        accountFilterPredicate={accountFilterPredicate}
                        onlyStandardCategories={onlyStandardCategories}
                        shouldShowNoAccountOption={shouldShowNoAccountOption}
                        shouldShowKickAccountOption={
                            shouldShowKickAccountOption
                        }
                        hideCoaAccountLastChangedBy={
                            hideCoaAccountLastChangedBy
                        }
                        valuePickersOnly={valuePickersOnly}
                        counterpartyEntityLabel={counterpartyEntityLabel}
                        counterpartyDirection={counterpartyDirection}
                        counterpartySingleSelect={counterpartySingleSelect}
                        counterpartyInvoicesOnly={counterpartyInvoicesOnly}
                        hasTaskCreatedByClientOption={
                            hasTaskCreatedByClientOption
                        }
                        hasTaskAssigneeOrgMembersOnly={
                            hasTaskAssigneeOrgMembersOnly
                        }
                        hasTaskAssignedToClientOption={
                            hasTaskAssignedToClientOption
                        }
                        ledgerFilterLabel={ledgerFilterLabel}
                        tabLabels={tabLabels}
                        draftStatusOptions={draftStatusOptions}
                        draftSourceOptions={draftSourceOptions}
                    />
                </AggregatedFilters>
            )}

            {staticActiveFilters}

            {filtersActive && (
                <ActiveFilters
                    activeFilters={orderedActiveFilters}
                    renderVisibleFilters={(visibleFilters) =>
                        visibleFilters.map((filterId) => (
                            <ActiveFilterButtonLabel
                                key={filterId}
                                filterId={filterId}
                                filters={filters}
                                onChange={onChange}
                                disabled={
                                    disabled ||
                                    !!disabledActiveFilters?.includes(filterId)
                                }
                                directionLabelVariant={
                                    directionRadios === glDirectionRadios
                                        ? "gl"
                                        : undefined
                                }
                                counterpartyEntityLabel={
                                    counterpartyEntityLabel
                                }
                                tabLabels={tabLabels}
                                draftStatusOptions={draftStatusOptions}
                                draftSourceOptions={draftSourceOptions}
                            />
                        ))
                    }
                    clearFilters={resetActiveFilters}
                    hideClearButton={hideClearButton || disabled}
                    maxVisibleFilters={maxVisibleFilters}
                    disabled={disabled}
                    filtersContainerRef={filtersContainerRef}
                    resolveFilterTab={(filterId) => {
                        const tab = ActiveFilterToFilterTabsMap[filterId];
                        if (VIRTUAL_FILTER_TABS.includes(tab)) {
                            return undefined;
                        }
                        return tab;
                    }}
                />
            )}
        </AggregatedFiltersProvider>
    );
};
